import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import MapSection from '../components/MapSection';
import SEO from '../components/SEO';

export default function About() {
  const { lang } = useLanguage();

  return (
    <div className="space-y-0">
      <SEO
        title={lang === 'da' ? 'Om Jette Keis Hansen' : 'About Jette Keis Hansen'}
        description={lang === 'da'
          ? 'Læs historien om Jette Keis Hansen, hendes 29 års erfaring med massage, filosofi om kropslig heling og nærværende tilgang til behandling.'
          : 'Read the story of Jette Keis Hansen, her 29 years of massage experience, healing philosophy, and mindful treatment approach.'
        }
      />
      
      {/* Page Header */}
      <section className="py-16 bg-[#FAF7F2] border-b border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl space-y-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
            {lang === 'da' ? 'Om Jette Keis Hansen' : 'About Jette Keis Hansen'}
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#2C2825]">
            {lang === 'da' ? '29 års erfaring med nærvær & massage' : '29 Years of Mindful Massage Therapy'}
          </h1>
          <p className="text-base sm:text-lg text-[#6E6760] leading-relaxed">
            {lang === 'da'
              ? 'En personlig fortælling om at lytte til kroppens sprog, løsne dybe spændinger og skabe et trygt rum for heling.'
              : 'A story of listening to the body’s wisdom, releasing deep tension, and holding space for genuine healing.'}
          </p>
        </div>
      </section>

      {/* Main Story & Philosophy */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Image Column */}
            <div className="lg:col-span-5 space-y-6">
              <div className="aspect-4/5 rounded-3xl overflow-hidden border border-[#E8E0D7] shadow-lg">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/massage-with-jette-dsc09348_2.jpg"
                  alt="Jette Keis Hansen massagebehandling"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="p-6 rounded-2xl bg-[#FAF7F2] border border-[#E8E0D7] space-y-3">
                <h3 className="font-serif-display text-xl font-bold text-[#2C2825]">
                  {lang === 'da' ? 'Uddannelse & Specialer' : 'Education & Specialties'}
                </h3>
                <ul className="space-y-2 text-xs text-[#6E6760]">
                  <li className="flex items-center gap-2 font-semibold text-[#2E3191]">
                    <i className="fa-solid fa-certificate text-[#2E3191]" />
                    <span>RAB godkendt - Anerkendt af sundhedsvæsenet</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#2E3191]" />
                    <span>Lægeeksamineret Massør (1998)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#2E3191]" />
                    <span>Specialist i Bindevævsmassage & Fysiurgisk Massage</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#2E3191]" />
                    <span>Biodynamisk Kranio-Sakral Terapi certificeret</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#2E3191]" />
                    <span>Ekspertise i Graviditets- og Efterfødselsbehandling</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-7 space-y-6 text-[#6E6760] leading-relaxed">
              
              <h2 className="font-serif-display text-3xl font-bold text-[#2C2825]">
                {lang === 'da' ? 'Kroppen er et arkiv over vore oplevelser' : 'The body is a living archive of experiences'}
              </h2>

              <p className="text-base">
                {lang === 'da'
                  ? 'Gennem næsten tre årtier som massør har jeg oplevet, hvordan stress, traumer og hverdagens belastninger sætter sig i vævet som spændinger, smerter eller stivhed. Når vi ikke lytter til kroppens spæde signaler, råber den til sidst igennem smerte.'
                  : 'Through nearly three decades as a massage therapist, I have witnessed how stress, trauma, and daily physical strains become embedded in muscle tissue as tension, pain, or restriction. When we ignore subtle bodily signals, the body eventually communicates through pain.'}
              </p>

              <p className="text-base">
                {lang === 'da'
                  ? 'Min behandlingstilgang bygger ikke på forhåndsbestemte skabeloner eller overfladiske løsninger. Hvert enkelt menneske, der lægger sig på min briks, mødes med en intuitiv, nærværende og lyttende berøring. Jeg anvender en kombination af bindevævsmassage og biodynamisk kranio-sakral terapi alt efter, hvad din krop kalder på i øjeblikket.'
                  : 'My treatment approach is never formulaic or superficial. Every person who steps onto my treatment table is met with an intuitive, listening touch. I integrate deep tissue massage and biodynamic craniosacral therapy depending on what your body calls for in the present moment.'}
              </p>

              {/* Prominent Deepak Chopra Quote */}
              <div className="my-8 p-8 rounded-2xl bg-[#EEF0F8] border border-[#2E3191]/30 space-y-3">
                <i className="fa-solid fa-quote-left text-3xl text-[#2E3191]/40" />
                <p className="font-serif-display text-2xl font-bold italic text-[#2C2825] leading-snug">
                  "Healing is the return of the memory of wholeness."
                </p>
                <p className="text-sm font-bold text-[#2E3191]">
                  – Deepak Chopra
                </p>
              </div>

              <h3 className="font-serif-display text-2xl font-bold text-[#2C2825] pt-2">
                {lang === 'da' ? 'Filosofi & Nærvær' : 'Philosophy & Presence'}
              </h3>

              <p className="text-base">
                {lang === 'da'
                  ? 'Når nervesystemet falder til ro, og bindevævet får lov at strække og åbne sig i sit eget tempo, aktiveres kroppens iboende evne til genopbygning og balance. Det handler ikke om at tvinge musklerne på plads, men om at invitere vævet til at give slip.'
                  : 'When the central nervous system settles into quietude and fascia is invited to unfold gently, the body naturally restores its innate balance. It is not about forcing muscles, but inviting tissue to let go.'}
              </p>

              <p className="text-base">
                {lang === 'da'
                  ? 'Jeg glæder mig til at byde dig velkommen i min klinik hos Salig på Masnedøgade 20 på Østerbro – eller besøge dig ved udekørende behandling.'
                  : 'I look forward to welcoming you to my clinic space at Salig on Masnedøgade 20 in Østerbro – or visiting you for an out-call treatment.'}
              </p>

              <div className="pt-4 flex items-center gap-4">
                <a
                  href="https://massage-with-jette.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 rounded-full bg-[#2E3191] text-white font-semibold hover:bg-[#1E2063] transition-all shadow-sm inline-flex items-center gap-2"
                >
                  <span>{lang === 'da' ? 'Book behandling hos Jette' : 'Book a session with Jette'}</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs opacity-80" />
                </a>

                <Link
                  to="/behandlinger"
                  className="px-6 py-4 rounded-full border border-[#E8E0D7] text-[#2C2825] font-semibold hover:border-[#2E3191] hover:text-[#2E3191] transition-all"
                >
                  {lang === 'da' ? 'Se alle behandlinger' : 'View all treatments'}
                </Link>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Map Section */}
      <MapSection />

    </div>
  );
}
